import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import * as OpenTripPlannerActions from './open-trip-planner.actions';
import { OpenTripPlannerService } from '../open-trip-planner.service';
import {
    Plan,
    PlannerErrorMessage,
    Route,
    RouteGroups,
    Stop,
    TransportMode,
    TripDetails
} from '../open-trip-planner.model';
import { DisruptionMessageResponse, deepCopy } from '@ridango/core';

@Injectable()
export class OpenTripPlannerEffects {
    constructor(
        private actions$: Actions,
        private openTripPlannerService: OpenTripPlannerService
    ) {}

    getAllRoutes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getAllRoutes),
            mergeMap(() =>
                this.openTripPlannerService.getAllRoutes().pipe(
                    map((routes: Route[]) => {
                        return OpenTripPlannerActions.getAllRoutesSuccess({ routes });
                    }),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getRoutesGroups$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getAllRoutesGroups),
            mergeMap(() =>
                this.openTripPlannerService.getRoutesGroups().pipe(
                    map((routesGroups: RouteGroups[]) => {
                        return OpenTripPlannerActions.getAllRoutesGroupsSuccess({ routesGroups });
                    }),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getAllStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getAllStops),
            mergeMap(() =>
                this.openTripPlannerService.getAllStops().pipe(
                    map((stops: Stop[]) => {
                        return OpenTripPlannerActions.getAllStopsSuccess({ stops });
                    }),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
            )
        );
    });

    getPlan$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getPlan),
            switchMap((payload) => {
                if (
                    payload?.fromPlace === payload?.toPlace ||
                    (payload?.fromPlace?.lat &&
                        payload?.fromPlace?.lng &&
                        payload?.fromPlace?.lat === payload?.toPlace?.lat &&
                        payload?.fromPlace?.lng === payload?.toPlace?.lng)
                ) {
                    return of(
                        OpenTripPlannerActions.getPlanError({
                            error: {
                                id: 403,
                                message: null,
                                msg: null
                            }
                        })
                    );
                }

                return this.openTripPlannerService.getPlan(payload).pipe(
                    switchMap((initialResponse: Plan) => {
                        const threshold = this.openTripPlannerService.env.JOURNEY_PLANNER?.LIMITED_TRIPS_THRESHOLD ?? 3;
                        const featureEnabled =
                            this.openTripPlannerService.env.JOURNEY_PLANNER?.EXPAND_SEARCH_FOR_LIMITED_TRIPS ?? false;

                        const shouldExpandSearch =
                            !payload.loadMore &&
                            !initialResponse.error &&
                            initialResponse.plan?.itineraries?.length <= threshold &&
                            featureEnabled;

                        if (shouldExpandSearch) {
                            const expandedPayload = {
                                ...payload,
                                expandSearchWindow: true
                            };

                            return this.openTripPlannerService.getPlan(expandedPayload).pipe(
                                map((expandedResponse: Plan) => {
                                    return expandedResponse.error ? initialResponse : expandedResponse;
                                }),
                                catchError(() => {
                                    return of(initialResponse);
                                })
                            );
                        }
                        return of(initialResponse);
                    }),
                    map((response: Plan) => {
                        if (!response.error) {
                            let itineraries = deepCopy(response.plan.itineraries).sort((a, b) => {
                                const aMinTime = Math.min(
                                    a.startTime,
                                    a.startTime + (a.journeyStartDepartureDelay || 0) * 1000
                                );
                                const bMinTime = Math.min(
                                    b.startTime,
                                    b.startTime + (b.journeyStartDepartureDelay || 0) * 1000
                                );
                                return aMinTime - bMinTime;
                            });

                            itineraries = itineraries.filter(
                                (itinerary) =>
                                    itinerary.legs.filter((leg) => leg.mode !== TransportMode.Walk).length > 0
                            );

                            itineraries.forEach((itinerary) => {
                                itinerary.legs.forEach((leg) => {
                                    leg.alerts = leg.alerts?.reduce((acc, alert) => {
                                        if (alert?.messages) {
                                            return [...acc, ...alert.messages];
                                        }
                                        return [...acc, alert];
                                    }, []);
                                });
                            });
                            return OpenTripPlannerActions.getPlanSuccess({ itineraries });
                        } else {
                            const error = response.error;
                            return OpenTripPlannerActions.getPlanError({ error });
                        }
                    }),
                    catchError((err) => {
                        return of(
                            OpenTripPlannerActions.getPlanError({
                                error: {
                                    id: err.status,
                                    message: PlannerErrorMessage.INTERNAL_ERROR,
                                    msg: err.message
                                }
                            })
                        );
                    })
                );
            })
        );
    });

    getSearchRecentlyJourneys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getAllSearchRecentlyJourneys),
            map(() => {
                const result = this.openTripPlannerService.getSearchRecentlyJourneys();

                return OpenTripPlannerActions.setSearchRecentlyJourneys({ result });
            })
        );
    });

    addSearchRecentlyJourneys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.addSearchRecentlyJourneyToStorage),
            map((payload) => {
                this.openTripPlannerService.addSearchRecentlyJourneys({
                    destination: payload.destination,
                    origin: payload.origin
                });

                return OpenTripPlannerActions.addSearchRecentlyJourney(payload);
            })
        );
    });

    clearSearchRecentlyJourneys$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.clearSearchRecentlyJourneysFromStorage),
            map(() => {
                this.openTripPlannerService.clearSearchRecentlyJourneys();

                return OpenTripPlannerActions.clearSearchRecentlyJourneys();
            })
        );
    });

    getNearbyStops$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getNearbyStops),
            switchMap((payload) => {
                return this.openTripPlannerService
                    .getStopsInRadius(payload.latitude, payload.longitude, payload.nearbyRadiusMeters)
                    .pipe(
                        map((nearbyStops: Stop[]) => OpenTripPlannerActions.getNearbyStopsSuccess({ nearbyStops })),
                        catchError(() => of(OpenTripPlannerActions.getNearbyStopsError()))
                    );
            })
        );
    });

    loadTripsByRouteId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OpenTripPlannerActions.loadTripsByRouteId),
            mergeMap((action) =>
                this.openTripPlannerService.getTripsByRouteId(action.routeId).pipe(
                    map((trips: TripDetails[]) => OpenTripPlannerActions.loadTripsByRouteIdSuccess({ trips })),
                    catchError((error) => of(OpenTripPlannerActions.loadTripsByRouteIdFailure({ error })))
                )
            )
        )
    );

    getAllAlerts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenTripPlannerActions.getAllAlerts),
            mergeMap(() =>
                this.openTripPlannerService.getAllAlerts().pipe(
                    map((response: DisruptionMessageResponse) => {
                        const alerts = response.messages;
                        return OpenTripPlannerActions.getAllAlertsSuccess({ alerts });
                    }),
                    catchError((error) => {
                        return of(OpenTripPlannerActions.getAllAlertsFailure({ error }));
                    })
                )
            )
        );
    });
}
